import React, { ReactElement, useEffect } from 'react';
import NewApartmentCard from './NewApartmentCard';
import { Link, makeStyles } from '@material-ui/core';
import { Link as RouterLink } from 'react-router-dom';
import { CardData } from '../../App';
import { loadingLength } from '../../constants/HomeConsts';
import { ApartmentWithId } from '../../../../common/types/db-types';
import { sortApartments } from '../../utils/sortApartments';

type Props = {
  data: CardData[];
  initialResultsToShow?: number;
  user: firebase.User | null;
  setUser: React.Dispatch<React.SetStateAction<firebase.User | null>>;
  onMoreResultsLoaded?: (results: number) => void;
  sortMethod?: keyof CardData | keyof ApartmentWithId | 'originalOrder';
  orderLowToHigh?: boolean;
};

const useStyles = makeStyles({
  container: {
    position: 'relative',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    // border: `1px solid black`,
    marginBottom: '16px',
  },
  titleText: {
    fontSize: '24px',
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: 'normal',
    letterSpacing: '0.48px',
  },
  viewMoreButton: {
    textTransform: 'none',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '28px',
  },
  cardsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    columnGap: '6px',
    rowGap: '12px',
    padding: '4px',
    '@media (max-width: 600px)': {
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '4px',
      height: 'auto',
      maxHeight: 'none',
      padding: '0px',
      margin: '0 4px',
      '& > div > a > div': {
        width: '100% !important',
        maxWidth: '180px !important',
        padding: '6px !important',
        '& .MuiTypography-root': {
          '&:first-of-type': {
            fontSize: '11px !important',
            height: 'auto !important',
            lineHeight: '1.2 !important',
          },
          '&:nth-of-type(2)': {
            fontSize: '10px !important',
            height: 'auto !important',
            lineHeight: '1.2 !important',
          },
        },
      },
    },
  },
});

/**
 * SearchResultsPageApartmentCards Component
 *
 * @remarks
 * This component displays a vertically scrollable grid of apartment cards on the search results page.
 * The cards are arranged in a 3-column grid layout with fixed spacing. The container has a fixed height
 * and enables vertical scrolling with hidden scrollbars.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {CardData[]} props.data - Array of apartment data to display in cards.
 * @param {number} [props.initialResultsToShow] - Initial number of cards to display.
 * @param {firebase.User | null} props.user - The currently logged in Firebase user.
 * @param {React.Dispatch<React.SetStateAction<firebase.User | null>>} props.setUser - Function to update the user state.
 * @param {(results: number) => void} [props.onMoreResultsLoaded] - Callback when more results are loaded.
 * @param {keyof CardData | keyof ApartmentWithId | 'originalOrder'} [props.sortMethod='originalOrder'] - How to sort the apartment cards.
 * @param {boolean} [props.orderLowToHigh=false] - Sort direction - true for ascending, false for descending.
 * @returns {ReactElement} SearchResultsPageApartmentCards component.
 */
const ApartmentCards = ({
  data,
  initialResultsToShow,
  user,
  onMoreResultsLoaded,
  setUser,
  sortMethod = 'originalOrder',
  orderLowToHigh = false,
}: Props): ReactElement => {
  const { container, cardsContainer } = useStyles();

  useEffect(() => {
    if (onMoreResultsLoaded) {
      onMoreResultsLoaded(initialResultsToShow ?? loadingLength);
    }
  }, [initialResultsToShow, onMoreResultsLoaded]);

  return (
    <div className={container}>
      <div className={cardsContainer}>
        {data &&
          sortApartments(data, sortMethod, orderLowToHigh).map(
            ({ buildingData, numReviews, company, avgRating }, index) => {
              const { id } = buildingData;
              return (
                <Link
                  key={index}
                  {...{
                    to: `/apartment/${id}`,
                    style: { textDecoration: 'none', display: 'block' },
                    component: RouterLink,
                  }}
                >
                  <NewApartmentCard
                    numReviews={numReviews}
                    avgRating={avgRating ?? 0}
                    buildingData={buildingData}
                    company={company}
                    user={user}
                    setUser={setUser}
                  />
                </Link>
              );
            }
          )}
      </div>
    </div>
  );
};

export default ApartmentCards;
